import { useEffect, useMemo, useState } from "react";

import { Grid, TextField as MUITextField, Typography } from "@mui/material";
import MuiAutocomplete from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { AxiosResponse } from "axios";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import throttle from "lodash/throttle";
import queryString from "query-string";

import { axios } from "../api";

import type { AeronetV6ItemsResponse } from "../types";
import type { QuoteList, QuoteType } from "./types";

export interface QuoteAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  quoteType?: QuoteType;
  supplier_id?: string | number;
  setDivision?: boolean;
  quote_id?: number;
  disableCreateNew?: boolean;
}

export function QuoteAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(
  props: QuoteAutocompleteProps<QuoteList, Multiple, DisableClearable, FreeSolo>
) {
  const {
    quoteType = "",
    supplier_id = "",
    setDivision = false,
    quote_id,
    disableCreateNew = false,
    ...autocompleteProps
  } = props;
  const externalOnChange = (autocompleteProps as any).onChange;
  const { setFieldValue } = autocompleteProps.form;
  const [quotes, setQuotes] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState("");
  const [isInitData, setIsInitData] = useState<boolean>(true);
  const { name } = fieldToTextField(props as any);

  const fetchQuotes = useMemo(
    () =>
      throttle((value: string, callback: Function) => {
        const params = {
          filter: [
            JSON.stringify({ model: "Quote", field: "status_id", value: 6 }),
          ],
          size: 20,
        };

        if (quoteType !== "") {
          params["filter"].push(
            JSON.stringify({
              model: "Quote",
              field: "quote_type",
              op: "==",
              value: quoteType,
            })
          );
        }

        if (supplier_id) {
          params["filter"].push(
            JSON.stringify({
              model: "Quote",
              field: "supplier_id",
              op: "==",
              value: supplier_id,
            })
          );
        }

        if (value) {
          params.filter.push(
            JSON.stringify({ field: "id", op: "ilike", value: value })
          );
          params["sort"] = "id";
        }
        axios
          .get("/v6/quotes", {
            params: params,
            paramsSerializer: (params) => queryString.stringify(params),
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<QuoteList[]>>) => {
            callback(resp.data.items);
          })
          .catch((error) => {
            callback([]);
          });
      }, 200),
    [quoteType, supplier_id]
  );

  useEffect(() => {
    let active = true;

    fetchQuotes(inputValue, (results: ReadonlyArray<QuoteList>) => {
      if (active) {
        let newQuotes: Array<string | QuoteList> = [];

        if (results) {
          newQuotes = [...newQuotes, ...results];
        }

        if (!disableCreateNew) {
          // @ts-ignore
          newQuotes.unshift("Create New");
        }
        setQuotes(newQuotes);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetchQuotes, disableCreateNew]);

  useEffect(() => {
    let active = true;
    if (quote_id && isInitData) {
      fetchQuotes(`#${quote_id}`, (results: ReadonlyArray<QuoteList>) => {
        if (active) {
          const selectedQuote = quotes.find((quote) => quote.id === quote_id);
          if (selectedQuote) {
            setFieldValue("quote", selectedQuote);
            setFieldValue("quote_id", selectedQuote.id);
            setFieldValue("comment", selectedQuote.comment);
            if (setDivision) {
              setFieldValue("division", { id: selectedQuote.division_id });
            }
            setIsInitData(false);
          }
        }
      });
    }
    
    return () => {
      active = false;
    };
  }, [fetchQuotes, isInitData, quote_id, quotes, setDivision, setFieldValue]);

  return (
    <MuiAutocomplete
      {...fieldToAutocomplete(autocompleteProps)}
      fullWidth
      autoComplete
      filterOptions={(x) => x}
      includeInputInList
      limitTags={3}
      options={quotes}
      disableCloseOnSelect={!!props.multiple}
      onChange={(_event, newValue) => {
        setFieldValue("quote", newValue);
        if (
          newValue &&
          Object.prototype.hasOwnProperty.call(newValue, "id") &&
          // @ts-ignore
          newValue.id
        ) {
          // @ts-ignore
          setFieldValue("quote_id", newValue.id);
          setFieldValue("comment", "");
        } else {
          setFieldValue("quote_id", "");
        }
        if (setDivision) {
          if (newValue && typeof newValue !== "string") {
            // @ts-ignore
            setFieldValue("division", { id: newValue.division_id });
          } else {
            setFieldValue("division", null);
          }
        }
        // Call optional external onChange handler if provided (e.g. from RFQ forms)
        if (typeof externalOnChange === "function") {
          (externalOnChange as any)(_event, newValue);
        }
      }}
      onInputChange={(_event, newInputValue) => {
        setIsInitData(false);
        setInputValue(newInputValue);
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : `#${option.id!}`
      }
      isOptionEqualToValue={(option, value) => {
        try {
          return option.id === value.id;
        } catch (e) {
          return false;
        }
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <MUITextField
          {...params}
          label="Quote Number"
          error={props.form.touched[name!] && !!props.form.errors[name!]}
          // @ts-ignore
          helperText={props.form.touched[name!] && props.form.errors[name!]}
          fullWidth
        />
      )}
      renderOption={(props, option, state) => {
        if (typeof option === "string") {
          return <li {...props}>{option}</li>;
        } else {
          const matches = match(option.id.toString()!, state.inputValue);
          const quotes = parse(option.id.toString()!, matches);

          return (
            <li {...props}>
              <Grid container alignItems="center">
               
                <Grid item xs>
                  <Typography>
                    
                    {quotes.map((quote, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: quote.highlight ? 700 : 400,
                        }}
                      >
                        {quote.text}
                      </span>
                    ))}
                  </Typography>
                  {option.comment ? (
                    <Typography variant="body2" color="textSecondary">
                      "{option.comment}"
                    </Typography>
                  ) : (
                    <></>
                  )}
                </Grid>
              </Grid>
            </li>
          );
        }
      }}
    />
  );
}

QuoteAutocomplete.displayName = "FormikMaterialUIQuoteAutocomplete";
